import clsx from 'clsx'
import { HeaderTableItem, Icon, Pagination, Typography } from 'components'
import { useContextPeopleBatches } from 'context'
import { useSelect } from 'hooks'
import {
	ChildrenTableProps,
	Icons,
	PeopleMyPeopleItem,
	TypographyTypes
} from 'interfaces'
import { onPageChanged } from 'lib'
import dayjs from 'lib/dayjs'
import React from 'react'
import { FollowersTagCell } from './followersTagCell'
import { loginType } from './iconLoginType'

export const MyPeopleTable: React.FC<
	ChildrenTableProps<PeopleMyPeopleItem[]>
> = ({
	data,
	backendPages,
	customWidth,
	justify = 'center',
	bodyTableClassName,
	actionsButton,
	actionColumn = 'left',
	actionColumnWidth = 5,
	page,
	setPage,
	dataSelected,
	setDataSelected,
	borderTable,
	listLoginType,
	onShowingItemsChange,
	onShowingItems,
	selectAllUsers,
	onSelectAllUsers,
	onClearSelectAllUsers,
	totalPeople
}): JSX.Element => {
	const { setSelectedUsers, setOrganizationFollower, setItemUser } =
		useContextPeopleBatches()
	/* eslint-disable @typescript-eslint/no-non-null-assertion */
	const { selectAll, handleSelectAll, handleSelected } = useSelect(
		data!,
		dataSelected!,
		setDataSelected!,
		'organization_follower_id'
	)
	/* eslint-enable @typescript-eslint/no-non-null-assertion */

	const onSelectAll = (): void => {
		const selectedIds = data?.map?.(user => user.user_id) ?? []
		const selectedUsers = data?.map?.(user => user) ?? []
		const orgFollowersIds =
			data?.map?.(user => user.organization_follower_id) ?? []
		setSelectedUsers(prev => [...prev, ...selectedIds])
		if (selectAll) {
			setItemUser([])
			onClearSelectAllUsers?.()
		} else {
			setItemUser(prev => {
				const existingUserIds = new Set(prev.map(user => user.user_id))
				const newUsers = selectedUsers.filter(
					user => !existingUserIds.has(user.user_id)
				)
				return [...prev, ...newUsers]
			})
		}
		setOrganizationFollower(prev => [...prev, ...orgFollowersIds])
		handleSelectAll()
	}

	const handleChangeCheckBox = (elements: PeopleMyPeopleItem): void => {
		if (dataSelected?.includes(elements.organization_follower_id)) {
			setSelectedUsers(prev =>
				prev.filter(element => element !== elements.user_id)
			)
			setItemUser(prev =>
				prev.filter(item => item.user_id !== elements.user_id)
			)
		} else {
			setItemUser(prev => [...prev, elements])
			setSelectedUsers(prev => [...prev, elements.user_id])
		}
	}

	return (
		<div className="flex gap-5 w-full mt-4">
			<div className="min-w-[1300px] md:w-full lg:w-full">
				<div
					className={clsx(
						!borderTable && 'border-2 border-gray-7 rounded-lg md:w-full',
						'p-4 bg-white '
					)}
				>
					{selectAll &&
						!selectAllUsers &&
						totalPeople &&
						data &&
						totalPeople > data.length && (
							<div className="bg-blue-primary/10 border border-blue-primary/30 rounded px-4 py-2 flex items-center justify-center gap-1 text-sm text-gray-9 mb-2">
								<span>All {data.length} people on this page are selected.</span>
								<button
									type="button"
									className="font-semibold underline text-blue-primary hover:text-blue-secondary ml-1"
									onClick={onSelectAllUsers}
								>
									Select all {totalPeople} people
								</button>
							</div>
						)}
					{selectAllUsers && totalPeople && (
						<div className="bg-blue-primary/10 border border-blue-primary/30 rounded px-4 py-2 flex items-center justify-center text-sm text-gray-9 mb-2">
							All {totalPeople} people are selected.
						</div>
					)}
					<table className="w-full md:overflow-y-hidden md:overflow-x-scroll hide-scroll-bar justify-center">
						<thead className="flex w-full h-5 mb-3 pr-5">
							<tr className="w-full flex justify-between items-center">
								{dataSelected && setDataSelected && (
									<th
										scope="col"
										className="flex items-center justify-center"
										style={{ flexBasis: `3%` }}
									>
										<input
											type="checkbox"
											className="h-4 w-4 rounded border-gray-1 text-blue-primary focus:ring-transparent-default cursor-pointer"
											checked={!!selectAllUsers || selectAll}
											onChange={onSelectAll}
										/>
									</th>
								)}
								{actionsButton && actionColumn === 'left' && (
									<th
										scope="col"
										className="flex items-center justify-center text-[15px] leading-[18px] text-gray-9 font-bold cursor-pointer"
										style={{ flexBasis: `${actionColumnWidth}%` }}
									>
										<Typography
											title="Action"
											fontLeading="15/18"
											type={TypographyTypes.span}
											className="text-gray-9"
										/>
									</th>
								)}
								{data && (
									<>
										<HeaderTableItem
											header="Name"
											justify={justify}
											table="myPeople"
											column="full_name"
											style={
												customWidth
													? { flexBasis: `${customWidth[0]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>
										<HeaderTableItem
											header="Status"
											justify={justify}
											table="myPeople"
											column="status"
											style={
												customWidth
													? { flexBasis: `${customWidth[1]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="ID"
											justify={justify}
											table="myPeople"
											style={
												customWidth
													? { flexBasis: `${customWidth[2]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="Email"
											justify={justify}
											table="myPeople"
											column="email"
											style={
												customWidth
													? { flexBasis: `${customWidth[3]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="Zip/Postal"
											justify={justify}
											table="myPeople"
											column="postal_code"
											style={
												customWidth
													? { flexBasis: `${customWidth[4]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="Sign up Date"
											justify={justify}
											table="myPeople"
											column="last_login"
											style={
												customWidth
													? { flexBasis: `${customWidth[5]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<th
											scope="col"
											className={clsx(
												'flex items-center text-gray-9 cursor-pointer',
												justify === 'center' && 'justify-center',
												justify === 'left' && 'justify-left',
												justify === 'right' && 'justify-right'
											)}
											style={
												customWidth
													? { flexBasis: `${customWidth[6]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										>
											<Typography
												title="Follower Tag"
												fontLeading="15/18"
												type={TypographyTypes.span}
												className="text-gray-9"
											/>
										</th>
										<HeaderTableItem
											header="OS"
											justify={justify}
											table="myPeople"
											column="device_type"
											style={
												customWidth
													? { flexBasis: `${customWidth[7]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>
										<HeaderTableItem
											header="Login"
											justify={justify}
											table="myPeople"
											style={
												customWidth
													? { flexBasis: `${customWidth[8]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="Push On"
											justify={justify}
											table="myPeople"
											column="notification_push"
											style={
												customWidth
													? { flexBasis: `${customWidth[9]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>

										<HeaderTableItem
											header="Loc On"
											justify={justify}
											table="myPeople"
											column="active_location"
											style={
												customWidth
													? { flexBasis: `${customWidth[9]}%` }
													: { flexBasis: `${100 / data.length}%` }
											}
										/>
									</>
								)}
								{actionsButton && actionColumn === 'right' && (
									<th
										scope="col"
										className="flex items-center justify-center text-[15px] leading-[18px] text-gray-9 font-bold cursor-pointer"
										style={{ flexBasis: `12%` }}
									>
										<Typography
											title="Actions"
											fontLeading="15/18"
											type={TypographyTypes.span}
											className="text-gray-9"
										/>
									</th>
								)}
							</tr>
						</thead>
						<tbody
							className={clsx(
								bodyTableClassName,
								'!h-[calc(100vh_-_28rem)] overflow-y-scroll hide-scroll-bar w-full  flex flex-col gap-2'
							)}
						>
							{data?.map?.(item => (
								<tr
									key={item.organization_follower_id}
									className="w-full flex justify-between"
								>
									{dataSelected && setDataSelected && (
										<td
											className="flex justify-center items-center py-2"
											style={{ flexBasis: `3%` }}
										>
											<input
												type="checkbox"
												className="h-4 w-4 rounded border-gray-1 text-blue-primary focus:ring-transparent-default cursor-pointer"
												checked={
													!!selectAllUsers ||
													dataSelected.includes(item.organization_follower_id)
												}
												onChange={() => {
													handleChangeCheckBox(item)
													handleSelected(item.organization_follower_id)
													setOrganizationFollower(prev => [
														...prev,
														item.organization_follower_id
													])
													onClearSelectAllUsers?.()
												}}
											/>
										</td>
									)}
									{actionsButton && actionColumn === 'left' && (
										<td
											className={clsx(
												'h-10 flex justify-center items-center text-[15px] leading-[18px] text-center text-gray-9'
											)}
											style={{ flexBasis: `${actionColumnWidth}%` }}
										>
											{React.cloneElement(actionsButton, {
												value: item.organization_follower_id,
												data: item
											})}
										</td>
									)}
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9 truncate max-w-auto',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[0]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="truncate">{item.full_name}</div>
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9 capitalize',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[1]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										{item.status}
									</td>

									<td
										className={clsx(
											' w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9  truncate max-w-auto',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[2]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="truncate">
											{item.member_id !== null ? item.member_id : 'N/A'}
										</div>
									</td>

									<td
										className={clsx(
											'h-10 flex items-center text-[15px] leading-[18px] text-gray-9 mx-auto  truncate ',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[3]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="truncate">{item.email}</div>
									</td>

									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[4]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										{item.postal_code}
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[5]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										{dayjs(item.last_login).format('MM/DD/YYYY')}
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[6]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<FollowersTagCell
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											followerTags={item.followers_tag as any}
										/>
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[7]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="w-5 h-5">
											<Icon
												src={
													item.device_type
														? loginType[item.device_type]
														: Icons.cancel
												}
												fillPath
												className="text-gray-3"
											/>
										</div>
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[8]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<span className="relative w-8">
											{item.user_signin_type.length > 1 && listLoginType && (
												<td>
													{React.cloneElement(listLoginType, {
														data: item.user_signin_type
													})}
												</td>
											)}

											<div className="w-5 h-5">
												<Icon
													src={
														item.user_signin_type[0]
															? loginType[item.user_signin_type[0].signin_type]
															: Icons.block
													}
													fillPath={
														!(
															item.user_signin_type[0] &&
															item.user_signin_type[0].signin_type === 'google'
														)
													}
													className="text-gray-3"
												/>
											</div>
										</span>
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[9]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="w-4 h-4">
											<Icon
												src={
													item.notification_push
														? Icons.checkCircle
														: Icons.cancel
												}
												fillPath
												className="text-gray-3"
											/>
										</div>
									</td>
									<td
										className={clsx(
											'w-full h-10 flex items-center text-[15px] leading-[18px] text-gray-9',
											justify === 'center' && 'justify-center text-center',
											justify === 'left' && 'justify-left text-left',
											justify === 'right' && 'justify-right text-right'
										)}
										style={
											customWidth
												? { flexBasis: `${customWidth[9]}%` }
												: { flexBasis: `${100 / data.length}%` }
										}
									>
										<div className="w-4 h-4">
											<Icon
												src={
													item.active_location
														? Icons.checkCircle
														: Icons.cancel
												}
												fillPath
												className="text-gray-3"
											/>
										</div>
									</td>

									{actionsButton && actionColumn === 'right' && (
										<td
											className={clsx(
												'h-10 flex justify-center items-center text-[15px] leading-[18px] text-center text-gray-9'
											)}
											style={{ flexBasis: `12%` }}
										>
											{React.cloneElement(actionsButton, {
												value: item.organization_follower_id
											})}
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="w-full flex items-center justify-end">
					<Pagination
						totalRecords={backendPages || 1}
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						onPageChanged={pages => onPageChanged(pages, setPage!)}
						page={page}
						pageLimit={1}
						itemsShowing={onShowingItems}
						onShowingItemsChange={onShowingItemsChange}
					/>
				</div>
			</div>
		</div>
	)
}
